import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

// =============================================================================
// Mi Ruta moderation context
// -----------------------------------------------------------------------------
// Data layer for the PR Local route & comment moderation dashboard. Talks to the
// route-moderation package on the admin Go backend, mounted under both /admin
// and /master:
//
//   {API}/{admin|master}/route-moderation/api/v1/moderation/{summary,filters,
//         routes,routes/{id},routes/{id}/moderation,
//         comments,comments/{id}/moderation,comments/{id},
//         route-reports,route-reports/{id}/resolve,
//         comment-reports,comment-reports/{id}/resolve}
//
// The reads are filtered, paged queues over the routes people publish from the
// app and the comment threads under them. The writes are deliberately few:
// a route's moderation_status, a comment's visible/hidden flag, a comment
// tombstone, and a verdict on a report. Nothing here edits a user's words.
//
// Handlers return the payload directly (no {success,data} envelope), but we
// unwrap defensively so a wrapped response would also work.
// =============================================================================

const RouteModerationContext = createContext();

export const useRouteModeration = () => {
  const context = useContext(RouteModerationContext);
  if (!context) {
    throw new Error('useRouteModeration must be used within a RouteModerationProvider');
  }
  return context;
};

export const RouteModerationProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';

  // Pick the scope for the current login (dual admin/master access).
  const scope = useCallback(() => {
    if (loginType === 'master') return 'master';
    if (loginType === 'admin') return 'admin';
    throw new Error('Invalid login type for route moderation access');
  }, [loginType]);

  const moderationBase = useCallback(
    () => `${API_BASE_URL}/${scope()}/route-moderation/api/v1/moderation`,
    [API_BASE_URL, scope],
  );

  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can moderate routes and comments');
    }
  }, [loginType]);

  // The backend answers a rejected write with {error, fields:{…}} — surface the
  // per-field message so a moderator sees which value was wrong, not "failed".
  const handleApiResponse = async (response) => {
    let data = null;
    const text = await response.text();
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = { raw: text };
      }
    }
    if (!response.ok) {
      let msg = (data && (data.error || data.message)) || 'Request failed';
      if (data && data.fields) {
        const detail = Object.values(data.fields).filter(Boolean).join(' ');
        if (detail) msg = `${msg}: ${detail}`;
      }
      const err = new Error(msg);
      err.status = response.status;
      err.data = data;
      throw err;
    }
    return data;
  };

  const clearError = useCallback(() => setError(null), []);

  // Wrap loading/error handling around one request so each helper stays tiny.
  const run = useCallback(async (fn, fallbackMsg) => {
    validateAccess();
    setLoading(true);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      const msg = err.message || fallbackMsg;
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, [validateAccess]);

  const buildQuery = (options = {}, keys = []) => {
    const params = new URLSearchParams();
    keys.forEach((k) => {
      const v = options[k];
      if (v !== undefined && v !== null && v !== '') params.append(k, v);
    });
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  };

  const unwrap = (res) => (res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res);

  const writeJSON = useCallback((url, method, body) => makeAuthenticatedRequest(url, {
    method,
    headers: { 'Content-Type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  }), [makeAuthenticatedRequest]);

  // ── Queue rollup & filter options ───────────────────────────────────────────

  const fetchModerationSummary = useCallback(() => run(async () =>
    unwrap(await handleApiResponse(await makeAuthenticatedRequest(`${moderationBase()}/summary`))),
  'Failed to fetch moderation summary'), [run, makeAuthenticatedRequest, moderationBase]);

  const fetchModerationFilters = useCallback(() => run(async () =>
    unwrap(await handleApiResponse(await makeAuthenticatedRequest(`${moderationBase()}/filters`))),
  'Failed to fetch moderation filters'), [run, makeAuthenticatedRequest, moderationBase]);

  // ── Routes ──────────────────────────────────────────────────────────────────

  // Returns the full { routes, total_count, … } envelope.
  const fetchRoutes = useCallback((options = {}) => run(async () => {
    const qs = buildQuery(options, [
      'moderation_status', 'visibility', 'city', 'region', 'owner_user_id',
      'search', 'reported', 'sort', 'limit', 'offset',
    ]);
    return unwrap(await handleApiResponse(await makeAuthenticatedRequest(`${moderationBase()}/routes${qs}`)));
  }, 'Failed to fetch routes'), [run, makeAuthenticatedRequest, moderationBase]);

  // One route with its stops, reports and thread.
  const fetchRouteDetail = useCallback((routeId) => run(async () =>
    unwrap(await handleApiResponse(await makeAuthenticatedRequest(
      `${moderationBase()}/routes/${encodeURIComponent(routeId)}`,
    ))),
  'Failed to fetch route detail'), [run, makeAuthenticatedRequest, moderationBase]);

  // status: 'pending' | 'approved' | 'rejected'. Rejecting also drops the route
  // off the trending board.
  const setRouteModeration = useCallback((routeId, status, note) => run(async () =>
    unwrap(await handleApiResponse(await writeJSON(
      `${moderationBase()}/routes/${encodeURIComponent(routeId)}/moderation`,
      'PUT',
      { moderation_status: status, moderation_note: note ?? null },
    ))),
  'Failed to update route moderation'), [run, writeJSON, moderationBase]);

  // ── Comments ────────────────────────────────────────────────────────────────

  // Returns the full { comments, total_count, … } envelope.
  const fetchComments = useCallback((options = {}) => run(async () => {
    const qs = buildQuery(options, [
      'moderation_status', 'route_id', 'user_id', 'search',
      'reported', 'include_deleted', 'sort', 'limit', 'offset',
    ]);
    return unwrap(await handleApiResponse(await makeAuthenticatedRequest(`${moderationBase()}/comments${qs}`)));
  }, 'Failed to fetch comments'), [run, makeAuthenticatedRequest, moderationBase]);

  // status: 'visible' | 'hidden'. Reversible; the route's comment_count comes
  // back recomputed.
  const setCommentModeration = useCallback((commentId, status) => run(async () =>
    unwrap(await handleApiResponse(await writeJSON(
      `${moderationBase()}/comments/${encodeURIComponent(commentId)}/moderation`,
      'PUT',
      { moderation_status: status },
    ))),
  'Failed to update comment moderation'), [run, writeJSON, moderationBase]);

  // Tombstone: the text goes for good, the row stays so replies keep their place.
  const deleteComment = useCallback((commentId) => run(async () =>
    unwrap(await handleApiResponse(await writeJSON(
      `${moderationBase()}/comments/${encodeURIComponent(commentId)}`,
      'DELETE',
    ))),
  'Failed to delete comment'), [run, writeJSON, moderationBase]);

  // ── Report queues ───────────────────────────────────────────────────────────

  const fetchRouteReports = useCallback((options = {}) => run(async () => {
    const qs = buildQuery(options, ['resolved', 'reason', 'route_id', 'limit', 'offset']);
    return unwrap(await handleApiResponse(await makeAuthenticatedRequest(`${moderationBase()}/route-reports${qs}`)));
  }, 'Failed to fetch route reports'), [run, makeAuthenticatedRequest, moderationBase]);

  const fetchCommentReports = useCallback((options = {}) => run(async () => {
    const qs = buildQuery(options, ['resolved', 'reason', 'comment_id', 'limit', 'offset']);
    return unwrap(await handleApiResponse(await makeAuthenticatedRequest(`${moderationBase()}/comment-reports${qs}`)));
  }, 'Failed to fetch comment reports'), [run, makeAuthenticatedRequest, moderationBase]);

  // A verdict closes every open report on the same target, so the queue does not
  // re-ask a question that has been answered. With applyAction, 'upheld' also
  // rejects the route / hides the comment.
  const resolveRouteReport = useCallback((reportId, { verdict, applyAction = false, note } = {}) => run(async () =>
    unwrap(await handleApiResponse(await writeJSON(
      `${moderationBase()}/route-reports/${encodeURIComponent(reportId)}/resolve`,
      'PUT',
      { verdict, apply_action: applyAction, note: note ?? null },
    ))),
  'Failed to resolve route report'), [run, writeJSON, moderationBase]);

  const resolveCommentReport = useCallback((reportId, { verdict, applyAction = false } = {}) => run(async () =>
    unwrap(await handleApiResponse(await writeJSON(
      `${moderationBase()}/comment-reports/${encodeURIComponent(reportId)}/resolve`,
      'PUT',
      { verdict, apply_action: applyAction },
    ))),
  'Failed to resolve comment report'), [run, writeJSON, moderationBase]);

  const value = {
    loading,
    error,
    clearError,
    fetchModerationSummary,
    fetchModerationFilters,
    fetchRoutes,
    fetchRouteDetail,
    setRouteModeration,
    fetchComments,
    setCommentModeration,
    deleteComment,
    fetchRouteReports,
    fetchCommentReports,
    resolveRouteReport,
    resolveCommentReport,
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasRouteModerationAccess: loginType === 'admin' || loginType === 'master',
  };

  return (
    <RouteModerationContext.Provider value={value}>
      {children}
    </RouteModerationContext.Provider>
  );
};
